import classnames from 'classnames/bind'
import React, { useRef } from 'react'

import {
  useIsomorphicLayoutEffect,
  useTimeline,
} from '@unlikelystudio/react-hooks'

import useUpdateLayoutEffect from '~/hooks/useUpdateLayoutEffect'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type LineThemes =
  | 'primary'
  | 'secondary'
  | 'black'
  | 'manatee'
  | 'inherit'
export interface LineProps {
  className?: string
  theme?: LineThemes
  thickness?: 1 | 2
  isVisible?: boolean
  initialLineState?: 'underlined' | 'none'
}
/**
 * Line component
 * @param props
 * @returns Component
 */
function Line({
  className,
  theme,
  isVisible,
  initialLineState,
  thickness,
}: LineProps) {
  const lineRef = useRef<HTMLDivElement>()

  const tl = useTimeline(
    { paused: false },
    (tl) => {
      if (initialLineState === 'underlined') {
        if (isVisible) {
          tl.fromTo(
            lineRef.current,
            { scaleX: 1, transformOrigin: 'center right' },
            {
              scaleX: 0,
              transformOrigin: 'center right',
              immediateRender: false,
              duration: 0.6,
              force3D: true,
              ease: 'expo.out',
            },
          )
        } else {
          tl.fromTo(
            lineRef.current,
            { scaleX: 0, transformOrigin: 'center left' },
            {
              scaleX: 1,
              transformOrigin: 'center left',
              immediateRender: false,
              duration: 0.5,
              force3D: true,
              ease: 'expo.out',
            },
          )
        }
      } else {
        if (isVisible) {
          tl.fromTo(
            lineRef.current,
            { scaleX: 0, transformOrigin: 'center left' },
            {
              scaleX: 1,
              transformOrigin: 'center left',
              immediateRender: false,
              duration: 0.6,
              force3D: true,
              ease: 'expo.out',
            },
          )
        } else {
          tl.fromTo(
            lineRef.current,
            { scaleX: 1, transformOrigin: 'center right' },
            {
              scaleX: 0,
              transformOrigin: 'center right',
              immediateRender: false,
              duration: 0.5,
              force3D: true,
              ease: 'expo.out',
            },
          )
        }
      }
    },
    [isVisible, initialLineState],
  )

  useIsomorphicLayoutEffect(() => {
    if (isVisible) {
      tl.play()
    }
  })

  useUpdateLayoutEffect(() => {
    if (
      (initialLineState === 'underlined' && isVisible) ||
      initialLineState !== 'underlined'
    ) {
      tl.restart()
    }
  }, [isVisible])

  return (
    <span
      style={{ height: `${thickness ?? 1}px` }}
      className={cx(className, css.Line, initialLineState, {
        [`theme-${theme}`]: theme,
      })}>
      <span ref={lineRef} className={css.lineProgress} />
    </span>
  )
}

Line.defaultProps = {
  theme: 'inherit',
  initialLineState: 'none',
}

export default Line
